import * as React from 'react';

import { MarketingFeeDetails, PriceSummary, PriceViewModel, RecentlyViewedModel } from 'models';
import { Theme, UitkThemeMap } from 'constants/theme';

import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkText } from 'uitk-react-text';

import { withInertRouter, WithInertRouterProps } from 'utils/withInertRouter';

import { LeadAndSubPrice } from 'components/common/pricing/lead-and-sub-price';
import { LeadPrice } from './lead-price';
import { MarketingFeeDetailsContainer } from './marketing-fee-details-container';
import { PriceBreakDown } from './price-breakdown';
import { PriceMessagesContainer } from './price-messages-container';
import { StrikeoutPriceAndInfoContainer } from './strikeout-price-and-info-container';

export interface ChildActionsOptions {
  trackingRfrr?: string;
  className?: string;
}

export interface UserSetPriceProps {
  index?: number;
  className: string | string[];
  diff?: string;
  messages?: string[];
  price?: PriceViewModel;
  hotelId?: number;
  priceFontWeight: string;
  wrapPrice?: boolean;
  id?: string;
  recentlyViewed?: RecentlyViewedModel;
  excludePriceBreakDownLink?: boolean;
  roomName?: string;
  ratePlanCode?: string;
  childActions?: (options: ChildActionsOptions) => React.ReactNode;
  lodgingPriceBreakDown?: PriceSummary;
}

type PriceProps = UserSetPriceProps & WithInertRouterProps;

interface AlignmentClasses {
  dialogId?: string;
  handleClose?: () => void;
  priceFontWeight: string;
  wrapPrice?: boolean;
  dialogType?: string;
  hotelId: number;
  marketingFeeDetails?: MarketingFeeDetails;
  viewed?: boolean;
  excludePriceBreakDownLink?: boolean;
  layerId: string;
}

type PriceViewModelWithAlignment = PriceViewModel & AlignmentClasses;
class PriceComponent extends React.Component<PriceProps> {
  public static defaultProps: Partial<PriceProps> = {
    excludePriceBreakDownLink: false,
  };

  public render() {
    const {
      index,
      className,
      price,
      messages,
      diff,
      hotelId,
      priceFontWeight,
      wrapPrice,
      id,
      recentlyViewed,
      excludePriceBreakDownLink,
      roomName,
      ratePlanCode,
      childActions,
      lodgingPriceBreakDown,
    } = this.props;

    if (!price) {
      return null;
    }

    const priceMessage = price && price.priceMessage;
    const pricelength = Object.keys(price).length; // for unavailable rateplan.
    const hasPriceMessaging = price.priceMessaging?.length > 0;
    const hasAdditionalMessages = !hasPriceMessaging && messages?.length > 0;

    const isPwaDialogNested = id === 'infosite-room-info-dialog-ratePlans';
    const layerId = ratePlanCode ? ratePlanCode : `${hotelId}-${index}`;
    const dialogId = `priceDisclaimerDialog-${layerId}`;
    const dialogType = isPwaDialogNested ? 'pwaDialogNested' : 'pwaDialog';
    const viewed = recentlyViewed !== null && recentlyViewed !== undefined;
    const priceBreakDownProps = {
      excludePriceBreakDownLink,
      lodgingPriceBreakDown,
      id,
      roomName,
      layerId,
      childActions,
    };

    return (
      <div data-stid="content-hotel-price" className={`price ${className || ''}`}>
        {!priceMessage && diff && <PriceDiff price={price} diff={diff} />}
        {!priceMessage && !diff && pricelength !== 1 && (
          <PriceValues
            {...price}
            {...{
              dialogId,
              priceFontWeight,
              wrapPrice,
              dialogType,
              hotelId,
              viewed,
              layerId,
            }}
            handleClose={this.routeStoreGoBack}
          />
        )}
        {hasAdditionalMessages && <SupportingMessages messages={messages} />}
        <PriceDetailsBreakdown {...priceBreakDownProps} />
        {price && pricelength === 1 && <PriceDescription text={price.lead} theme={Theme.URGENCY} />}
        {priceMessage && <PriceDescription {...priceMessage} />}
      </div>
    );
  }

  private routeStoreGoBack = () => {
    this.props.history.goBack();
  };
}

export const Price: React.FunctionComponent<UserSetPriceProps> = withInertRouter(PriceComponent);

const SupportingMessages = ({ messages }) => (
  <>
    {messages &&
      messages.length > 0 &&
      messages.map((message, index) => (
        <div
          className={`pwa-theme--grey-700 uitk-type-100 ${index === 0 ? 'uitk-type-bold' : ''}`}
          data-stid={`supporting-messages-${index}`}
          key={`supporting-messages-${index}`}
        >
          {message}
        </div>
      ))}
  </>
);

const PriceDetailsBreakdown = ({ excludePriceBreakDownLink, lodgingPriceBreakDown, id, roomName, layerId, childActions }) => (
  <>
    {!excludePriceBreakDownLink && lodgingPriceBreakDown && (
      <PriceBreakDown
        lodgingPriceBreakDown={lodgingPriceBreakDown}
        id={id}
        roomName={roomName}
        layerId={layerId}
        childActions={childActions}
      />
    )}
  </>
);

const PriceDiff = ({ price, diff }) => {
  const { lead, label, numberOfNightsAndRooms } = price;

  return (
    <React.Fragment>
      <UitkLayoutGrid>
        <UitkLayoutGridItem>
          <UitkText size={500} weight="bold">
            <span data-stid="content-hotel-display-price">{diff}</span>
          </UitkText>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
      {lead && label && (
        <span className="uitk-type-100 uitk-type-medium pwa-theme--grey-700" data-stid="content-hotel-lead-label">
          {lead}
          {label}
        </span>
      )}
      {numberOfNightsAndRooms && (
        <div
          className="numberOfNightsAndRooms pwa-theme--grey-700 uitk-type-100 uitk-type-regular"
          data-stid="content-hotel-number-nights-rooms"
        >
          {numberOfNightsAndRooms}
        </div>
      )}
    </React.Fragment>
  );
};
PriceDiff.displayName = 'PriceDiff';

const PriceValues = ({
  lead,
  strikeOut,
  label,
  numberOfNightsAndRooms,
  strikeoutDisclaimer,
  dialogId,
  handleClose,
  priceFontWeight,
  wrapPrice,
  dialogType,
  marketingFeeDetails,
  hotelId,
  viewed,
  priceMessages,
  priceMessaging,
  options,
  layerId,
}: PriceViewModelWithAlignment) => (
  <>
    {options && options.length > 0 ? (
      <UitkLayoutGrid>
        {options.map((option, index) => (
          <LeadAndSubPrice
            key={index}
            {...{
              option,
              strikeoutDisclaimer,
              dialogId,
              dialogType,
              handleClose,
              wrapPrice,
              priceFontWeight,
              viewed,
              label,
              layerId,
            }}
          />
        ))}
      </UitkLayoutGrid>
    ) : (
      <UitkLayoutGrid alignItems="center">
        {strikeOut && <StrikeoutPriceAndInfoContainer {...{ strikeoutDisclaimer, strikeOut, dialogId, wrapPrice, dialogType }} />}
        <LeadPrice {...{ label, lead, priceFontWeight, wrapPrice, strikeOut, viewed }} />
      </UitkLayoutGrid>
    )}

    <PriceMessagesContainer {...{ priceMessages, priceMessaging, numberOfNightsAndRooms }} />

    <MarketingFeeDetailsContainer {...{ marketingFeeDetails, hotelId, handleClose }} />
  </>
);
PriceValues.displayName = 'PriceValues';

const PriceDescription = ({ text, theme }: PriceViewModel['priceMessage']) => {
  if (!text) {
    return null;
  }

  return (
    <UitkText size={300} inline={true} dataTestId="content-hotel-sold-out" weight="medium" theme={UitkThemeMap[theme]}>
      {text}
    </UitkText>
  );
};
PriceDescription.displayName = 'PriceDescription';
